/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { Link } from '@tanstack/react-router'
import type { ComponentProps, ReactNode } from 'react'
import { forwardRef } from 'react'

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { cn } from '@/lib/utils'

export type ProfileBannerClassNames = {
  avatar?: string
  name?: string
  meta?: string
}

export interface ProfileBannerProps extends ComponentProps<'div'> {
  to?: ComponentProps<typeof Link>['to']
  params?: ComponentProps<typeof Link>['params']
  avatarChild?: ReactNode
  blurImage?: boolean
  classNames?: ProfileBannerClassNames
  isOnline?: boolean
  leftLowerChild?: ReactNode
  leftUpperChild?: ReactNode
  lowerChild?: string | ReactNode
  missingProfileImageUrl?: string
  profileImageUrl?: string
  profileName?: string
  rightChild?: ReactNode
  rightLowerChild?: ReactNode
  rightUpperChild?: ReactNode
  shape?: 'rounded' | 'square' | 'full'
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl'
  profilePhotoAlt?: string
  missingPhotoAlt?: string
}

const NAME_SPLIT = /\s+/

export const PROFILE_BANNER_SIZE_CLASSES: Record<
  NonNullable<ProfileBannerProps['size']>,
  { avatar: string; name: string; meta: string }
> = {
  xs: { avatar: 'size-6', name: 'text-xs', meta: 'text-xs' },
  sm: { avatar: 'size-9', name: 'text-sm', meta: 'text-sm' },
  md: { avatar: 'size-10', name: 'text-base', meta: 'text-sm' },
  lg: { avatar: 'size-11', name: 'text-base', meta: 'text-sm' },
  xl: { avatar: 'size-12', name: 'text-base', meta: 'text-sm' },
  '2xl': { avatar: 'size-14', name: 'text-base', meta: 'text-sm' },
  '3xl': { avatar: 'size-16', name: 'text-base', meta: 'text-sm' },
  '4xl': { avatar: 'size-18', name: 'text-base', meta: 'text-sm' },
}

function initialsFromName(name?: string) {
  if (!name?.trim()) {
    return ''
  }
  const parts = name.trim().split(NAME_SPLIT)
  return parts
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('')
}

export const ProfileBanner = forwardRef<HTMLDivElement, ProfileBannerProps>((props, forwardedRef) => {
  const {
    avatarChild,
    blurImage = false,
    isOnline = true,
    leftLowerChild,
    leftUpperChild,
    lowerChild,
    missingProfileImageUrl,
    profileImageUrl,
    profileName,
    rightChild,
    rightLowerChild,
    rightUpperChild,
    shape = 'full',
    size = 'md',
    profilePhotoAlt = 'Profile Photo',
    missingPhotoAlt = 'Missing Profile Photo',
    to,
    params,
    className,
    classNames,
    ...rest
  } = props

  const sizes = PROFILE_BANNER_SIZE_CLASSES[size]
  // Avatar primitives default to rounded-full + after:rounded-full; equal-specificity utilities
  // do not reliably override. Use important modifiers so square / rounded shapes win.
  const shapeRadius =
    shape === 'rounded'
      ? {
          root: '!rounded-md after:!rounded-md',
          clip: '!rounded-md',
        }
      : shape === 'square'
        ? {
            root: '!rounded-none after:!rounded-none',
            clip: '!rounded-none',
          }
        : {
            root: '!rounded-full after:!rounded-full',
            clip: '!rounded-full',
          }

  const outlineClass = isOnline ? 'outline outline-[2px] outline-green-500 outline-offset-1' : 'outline-transparent'

  const avatarInner = (
    <Avatar className={cn(sizes.avatar, shapeRadius.root, 'border border-border shadow-none', classNames?.avatar)}>
      {profileImageUrl ? (
        <AvatarImage
          alt={profilePhotoAlt}
          className={cn(shapeRadius.clip, blurImage && 'blur-sm')}
          src={profileImageUrl}
        />
      ) : null}
      <AvatarFallback className={cn(shapeRadius.clip, 'bg-muted text-muted-foreground')}>
        {missingProfileImageUrl ? (
          // Must be a plain <img>; AvatarImage inside Fallback loops on Base UI 1.6+ status reset.
          <img
            alt={missingPhotoAlt}
            className={cn('size-full object-cover', shapeRadius.clip)}
            height={64}
            src={missingProfileImageUrl}
            width={64}
          />
        ) : (
          <span>{initialsFromName(profileName) || '?'}</span>
        )}
      </AvatarFallback>
    </Avatar>
  )

  const avatarWrap = (
    <span className={cn('relative inline-flex overflow-hidden', shapeRadius.clip, outlineClass)}>{avatarInner}</span>
  )

  return (
    <div
      className={cn('flex items-center', profileName ? 'w-full gap-2' : 'gap-0', className)}
      ref={forwardedRef}
      {...rest}
    >
      <div className="relative flex shrink-0 flex-col items-center">
        {to ? (
          <Link className="inline-flex" params={params} to={to}>
            {avatarWrap}
          </Link>
        ) : (
          avatarWrap
        )}
        {avatarChild}
      </div>

      <div className="flex min-w-0 flex-1 flex-col items-start gap-0">
        <div className="flex w-full items-center gap-1">
          {leftUpperChild && typeof leftUpperChild === 'string' ? (
            <p className={cn(sizes.name, 'line-clamp-1 break-all text-muted-foreground', classNames?.name)}>
              {leftUpperChild}
            </p>
          ) : (
            leftUpperChild
          )}

          {profileName ? (
            to ? (
              <Link
                className={cn(sizes.name, 'line-clamp-1 break-all font-semibold text-foreground', classNames?.name)}
                params={params}
                to={to}
              >
                {profileName}
              </Link>
            ) : (
              <span
                className={cn(sizes.name, 'line-clamp-1 break-all font-semibold text-foreground', classNames?.name)}
              >
                {profileName}
              </span>
            )
          ) : null}

          {rightUpperChild && typeof rightUpperChild === 'string' ? (
            <p className={cn(sizes.name, 'line-clamp-1 break-all text-muted-foreground', classNames?.name)}>
              {rightUpperChild}
            </p>
          ) : (
            rightUpperChild
          )}
        </div>

        {(leftLowerChild || lowerChild || rightLowerChild) && (
          <div className="flex flex-wrap items-center gap-1">
            {leftLowerChild && typeof leftLowerChild === 'string' ? (
              <p className={cn(sizes.meta, 'line-clamp-1 break-all text-muted-foreground', classNames?.meta)}>
                {leftLowerChild}
              </p>
            ) : (
              leftLowerChild
            )}

            {lowerChild && typeof lowerChild === 'string' ? (
              <p className={cn(sizes.meta, 'line-clamp-1 break-all text-muted-foreground', classNames?.meta)}>
                {lowerChild}
              </p>
            ) : (
              lowerChild
            )}

            {rightLowerChild && typeof rightLowerChild === 'string' ? (
              <p className={cn(sizes.meta, 'line-clamp-1 break-all text-muted-foreground', classNames?.meta)}>
                {rightLowerChild}
              </p>
            ) : (
              rightLowerChild
            )}
          </div>
        )}
      </div>

      {rightChild != null ? <div className="shrink-0">{rightChild}</div> : null}
    </div>
  )
})

ProfileBanner.displayName = 'ProfileBanner'
